import {
  Component,
  computed,
  effect,
  inject,
  Input,
  OnChanges,
  output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import {
  ProfissionalDataService,
  ProfissionalService,
  ServicoDataService,
  TipoServicoDataService,
  BardoContextDataService,
  NichoTermosService,
} from '@nomoh-monorepo/data-access-bardo-administrador';
import { BottomSheetComponent, DeviceService } from '@nomoh-monorepo/shared-common-ui';
import { IProfissional } from '@nomoh-monorepo/shared-types';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { MultiSelectModule } from 'primeng/multiselect';
import { TooltipModule } from 'primeng/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

interface ServicoProfissionalDetalhado {
  idRelacionamento: string;
  servicoId: string;
  nome: string;
  categoria: string;
}

import { CommonModule } from '@angular/common';

@Component({
  selector: 'lib-profissional-info',
  standalone: true,
  templateUrl: './profissional-info.component.html',
  styleUrls: ['./profissional-info.component.scss'],
  imports: [
    CommonModule,
    ButtonModule,
    TooltipModule,
    MultiSelectModule,
    ReactiveFormsModule,
    DialogModule,
    BottomSheetComponent,
    TranslateModule,
  ],
})
export class ProfissionalInfoComponent implements OnChanges {
  modalExclusaoAberto = signal(false);
  excluindo = signal(false);
  exibirTodosServicos = signal(false);

  //#region Inputs
  @Input() profissional: IProfissional | null = null;
  closed = output<void>();

  //#endregion

  //#region Injeção de Dependências
  private profissionalService = inject(ProfissionalService);
  private profissionalDataService = inject(ProfissionalDataService);
  private servicoDataService = inject(ServicoDataService);
  private tipoServicoDataService = inject(TipoServicoDataService);
  private confirmationService = inject(ConfirmationService);
  private translateService = inject(TranslateService);
  private nichoTermosService = inject(NichoTermosService);
  deviceService = inject(DeviceService);
  private contextoService = inject(BardoContextDataService);

  $nicho = this.nichoTermosService.$nicho;

  possuiPermissao(permissao: string) {
    return this.contextoService.possuiPermissao(permissao);
  }
  //#endregion

  //#region Propriedades

  private avatarPalette = [
    '#F97316',
    '#6D28D9',
    '#2563EB',
    '#0EA5E9',
    '#059669',
    '#EA580C',
  ];

  metricas = { cancelados: 0, atendidos: 0, confirmados: 0 };
  carregandoMetricas = signal(false);
  //#endregion

  //#region signals

  modalServicosAberto = signal(false);
  servicosDetalhados = signal<ServicoProfissionalDetalhado[]>([]);

  servicosLista = computed(() => {
    const todos = this.servicosDetalhados() || [];
    if (this.exibirTodosServicos()) {
      return todos;
    }
    return todos.slice(0, 3);
  });

  hasMaisServicos = computed(() => {
    return (this.servicosDetalhados()?.length || 0) > 3;
  });

  //#endregion

  constructor() {
    effect(() => {
      if (!this.profissional?.id) {
        return;
      }

      const profissional = this.profissionalDataService
        .$profissionais()
        .find((f) => f?.id == this.profissional?.id);

      if (profissional) {
        this.profissional = profissional;
        this.montarServicosDetalhados();
      }
    });
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['profissional']?.currentValue?.id) {
      this.metricas = { cancelados: 0, atendidos: 0, confirmados: 0 };
      this.servicosDetalhados.set([]);
      this.exibirTodosServicos.set(false);
      this.montarServicosDetalhados();

      this.carregandoMetricas.set(true);
      try {
        this.metricas = await this.profissionalService.buscarMetricas(
          this.profissional!.id
        );
      } finally {
        this.carregandoMetricas.set(false);
      }
    }
  }

  //#region get Exibição
  getAvatarColor(profissionalId?: string): string {
    if (!profissionalId) {
      return this.avatarPalette[0];
    }

    let hash = 0;
    for (let i = 0; i < profissionalId.length; i++) {
      hash = profissionalId.charCodeAt(i) + ((hash << 5) - hash);
    }

    const index = Math.abs(hash) % this.avatarPalette.length;
    return this.avatarPalette[index];
  }

  getIniciais(nome?: string | null): string {
    if (!nome) {
      return '';
    }
    return nome
      .split(' ')
      .filter(Boolean)
      .map((n) => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  }

  getImagemPerfilUrl(prof: IProfissional): string | null {
    if (!prof.imagemPerfil || !prof.typeImage) {
      return null;
    }
    return `data:${prof.typeImage};base64,${prof.imagemPerfil}`;
  }

  formatarHorario(horario?: string | null): string {
    if (!horario) {
      return '--:--';
    }

    const partes = horario.split(':');
    if (partes.length >= 2) {
      const hora = partes[0]?.padStart(2, '0') ?? '00';
      const minuto = partes[1]?.padStart(2, '0') ?? '00';
      return `${hora}:${minuto}`;
    }

    return horario;
  }

  formatarZeroEsquerda(valor: number): string {
    if (valor === undefined || valor === null) return '00';
    return valor < 10 ? `0${valor}` : `${valor}`;
  }

  toggleExibirTodosServicos() {
    this.exibirTodosServicos.update((v) => !v);
  }
  //#endregion

  //#region Ações para Serviços

  mostrarMensagemSemOpcoes(): boolean {
    if (!this.profissional) {
      return false;
    }
    const totalServicos = this.servicoDataService.$servicos().length;
    if (totalServicos === 0) {
      return false;
    }

    const vinculados = this.profissional.servicos?.length ?? 0;
    return vinculados >= totalServicos;
  }

  temServicosDisponiveis(): boolean {
    if (!this.profissional) {
      return true;
    }
    const totalServicos = this.servicoDataService.$servicos().length;
    if (totalServicos === 0) {
      return true;
    }

    const vinculados = this.profissional.servicos?.length ?? 0;
    return vinculados < totalServicos;
  }

  private montarServicosDetalhados(): void {
    const servicosVinculados = this.profissional?.servicos ?? [];
    if (!servicosVinculados.length) {
      this.servicosDetalhados.set([]);
      return;
    }

    const todosServicos = this.servicoDataService.$servicos();
    const tipos = this.tipoServicoDataService.$tiposServico();

    const resultado = servicosVinculados
      .map((relacao) => {
        const servico = todosServicos.find((s) => s.id === relacao.servicoId);
        const tipo = servico
          ? tipos.find((t) => t.id === servico.tipoServicoId)
          : undefined;

        return {
          idRelacionamento: relacao.id,
          servicoId: relacao.servicoId,
          nome: servico?.nome ?? `Serviço ${relacao.servicoId}`,
          categoria:
            tipo?.nome ??
            servico?.tipoServico?.nome ??
            'Categoria não informada',
        };
      })
      .sort((a, b) => a.nome.localeCompare(b.nome));

    this.servicosDetalhados.set(resultado);
  }
  //#endregion

  //#region exclusao de profissional

  confirmarExclusao(profissional: IProfissional, event: Event) {
    event.stopPropagation();
    if (this.deviceService.$isMobile()) {
      this.modalExclusaoAberto.set(true);
    } else {
      this.confirmationService.confirm({
        target: event.target as EventTarget,
        message: this.translateService.instant('BARDO_PROFISSIONAIS.CONFIRMAR_EXCLUSAO_MSG', {
          termo: this.$nicho().profissional.singular.toLowerCase(),
          nome: profissional.nome,
        }),
        header: this.translateService.instant('GLOBAL.CONFIRMAR_EXCLUSAO'),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this.translateService.instant('GLOBAL.SIM_EXCLUIR'),
        rejectLabel: this.translateService.instant('GLOBAL.CANCELAR'),
        acceptButtonStyleClass: 'p-button-danger',
        accept: () => this.excluirProfissional(profissional.id),
      });
    }
  }

  async excluirProfissional(id?: string) {
    if (!id) return;
    this.excluindo.set(true);
    try {
      await this.profissionalService.deletar(id);
      this.modalExclusaoAberto.set(false);
      this.closed.emit();
    } catch (err) {
      console.error('Erro ao excluir profissional', err);
    } finally {
      this.excluindo.set(false);
    }
  }
  //#endregion
}

